import axios, { AxiosError, AxiosInstance } from "axios";
import { api } from '../../../../api/axiosInstance';
import { CreateSlotDTO, Slot, SlotSummary, SlotSearchParams, GetSlotsResponse, GetPaginationInfoResponse, CheckSlotsExistsRequest, CheckSlotsExistsResponse, BatchUpdateSlotRequest, BatchUpdateSlotResponse, BatchDetailResponse } from "../../../Slot/Model/Slot";
import { compactSlotSearchParams } from "../../../Slot/Utils/compactSlotSearchParams";
import { SlotRepository } from "../../../Slot/Domain/Repositories/SlotRepository";
import { User } from "../../../User/Model/User";
import { GetAllSlotsResponse } from "../../../Slot/Domain/UseCase/GetAllSlotsUseCase";

const wrapError =
  (action: string) =>
  (err: AxiosError): never => {
    throw new Error(`${action}에 실패하였습니다. 관리자에게 문의 주세요.`);
  };

export class SlotRepositoryImpl implements SlotRepository {
  /** 공용 axios 인스턴스 */
  private http: AxiosInstance;

  constructor() {
    this.http = api;
  }

  /* ----------------- CRUD ----------------- */
  createSlot(dto: CreateSlotDTO): Promise<Slot> {
    return this.http
      .post<Slot>("/slots/", dto)
      .then((r) => r.data)
      .catch(wrapError("슬롯 생성"));
  }

  batchCreate(request: { count: number; slots: CreateSlotDTO[] }): Promise<Slot[]> {
    return this.http
      .post<Slot[]>("/slots/batch_create/", request)
      .then((r) => r.data)
      .catch(wrapError("슬롯 일괄 생성"));
  }

  updateSlot(id: number, dto: Partial<Slot>) {
    return this.http
      .patch<Slot>(`/slots/${id}/`, dto)
      .then((r) => r.data)
      .catch(wrapError("슬롯 수정"));
  }

  deleteSlot(id: number) {
    return this.http
      .delete(`/slots/${id}/`)
      .then(() => {})
      .catch(wrapError("슬롯 삭제"));
  }

  /* ----------------- 상태 변경 ----------------- */
  extendSlot(id: number, days: number) {
    return this.http
      .patch<Slot>(`/slots/${id}/extend/`, { days })
      .then((r) => r.data)
      .catch(wrapError("슬롯 연장"));
  }

  refundSlot(id: number) {
    return this.http
      .patch<Slot>(`/slots/${id}/refund/`)
      .then((r) => r.data)
      .catch(wrapError("슬롯 환불"));
  }

  /* ----------------- 배치 처리 ----------------- */
  batchExtend(ids: number[], days: number) {
    return this.http
      .post<{ extended_slots: number[] }>("/slots/batch_extend/", {
        slot_ids: ids,
        days,
      })
      .then((r) => r.data.extended_slots)
      .catch(wrapError("일괄 연장"));
  }

  batchRefund(ids: number[]) {
    return this.http
      .post<{ refunded_slots: number[] }>("/slots/batch_refund/", {
        slot_ids: ids,
      })
      .then((r) => r.data.refunded_slots)
      .catch(wrapError("일괄 환불"));
  }

  batchUpdate(request: BatchUpdateSlotRequest): Promise<BatchUpdateSlotResponse> {
    return this.http
      .post<BatchUpdateSlotResponse>("/slots/batch_update/", request)
      .then((r) => r.data)
      .catch(wrapError("일괄 수정"));
  }

  checkExists(request: CheckSlotsExistsRequest): Promise<CheckSlotsExistsResponse> {
    return this.http
      .post<CheckSlotsExistsResponse>("/slots/check_exists/", request)
      .then((r) => r.data)
      .catch(wrapError("슬롯 존재 여부 체크"));
  }

  getBatchDetail(slotIds: number[]): Promise<BatchDetailResponse> {
    return this.http
      .get<BatchDetailResponse>("/slots/batch_detail/", {
        params: { slot_ids: slotIds.join(",") },
      })
      .then((r) => r.data)
      .catch(wrapError("슬롯 상세 일괄 조회"));
  }

  /* ----------------- 새로운 조회 API ----------------- */
  getSlots(params?: SlotSearchParams, options?: { signal?: AbortSignal }): Promise<GetSlotsResponse> {
    const q = compactSlotSearchParams(params);
    return this.http
      .get<GetSlotsResponse>("/slots/", { params: q, signal: options?.signal })
      .then((r) => r.data)
      .catch((err: AxiosError) => {
        if (axios.isCancel(err)) {
          throw err;
        }
        return wrapError("슬롯 조회")(err);
      });
  }

  getPaginationInfo(params?: SlotSearchParams): Promise<GetPaginationInfoResponse> {
    const q = compactSlotSearchParams(params);
    return this.http
      .get<GetPaginationInfoResponse>("/slots/pagination_info/", { params: q })
      .then((r) => r.data)
      .catch(wrapError("페이지네이션 정보 조회"));
  }

  getAllSlots(me: User, active?: boolean): Promise<GetAllSlotsResponse> {
    const params: any = {};
    if (active !== undefined) {
      params.active = active;
    }
    
    return this.http
      .get<GetAllSlotsResponse>("/slots/all/", { params })
      .then((r) => r.data)
      .catch(wrapError("전체 슬롯 조회"));
  }

  getSlotRankHistory(slotId: number): Promise<any> {
    return this.http
      .get(`/slots/${slotId}/rank_history/`)
      .then((r) => r.data)
      .catch(wrapError("순위 이력 조회"));
  }

  /* ----------------- 기존 조회 API (하위 호환성) ----------------- */
  getSlotsLegacy(params?: { user?: number }): Promise<Slot[]> {
    return this.http
      .get<Slot[]>("/slots/", { params })
      .then((r) => r.data)
      .catch(wrapError("슬롯 조회"));
  }

  getSummary() {
    return this.http
      .get<SlotSummary>("/slots/summary/")
      .then((r) => r.data)
      .catch(wrapError("슬롯 통계 조회"));
  }

  /* ----------------- 엑셀 ----------------- */

  uploadExcel(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http
      .post("/slots/excel/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(() => {})
      .catch(wrapError("엑셀 업로드"));
  }
}
